<script setup lang="ts">
import type { Ref } from 'vue'
import { StickyRatesExchangeRate } from '~/components/widget/sticky-rates'

const route = useRoute()
const stickyRatesTriggerEl = inject<Ref<HTMLElement | null>>('stickyRatesTriggerEl', ref(null))

const { data: fetchedRates } = await useFetch('/api/rates/widgets-cached')

const CURRENCIES = ['usd', 'eur', 'kzt', 'rub', 'cny'] as const

// Number(null) is 0, which would render a missing rate as a real one.
const toRate = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null

  const parsed = Number(value)
  return isNaN(parsed) ? null : parsed
}

const data = computed(() =>
  Object.fromEntries(
    CURRENCIES.map((currency) => [
      currency,
      {
        buyValue: toRate(fetchedRates.value?.averageRatesToday?.[`buy_${currency}`]),
        sellValue: toRate(fetchedRates.value?.averageRatesToday?.[`sell_${currency}`])
      }
    ])
  )
)

// Currencies without data are dropped entirely rather than shown as zeros.
const fitData = computed(() =>
  Object.fromEntries(
    Object.entries(data.value).filter(
      ([, val]) => val.buyValue !== null && val.sellValue !== null
    )
  )
)

// Render the bar on the server for non-home pages so the header doesn't grow after hydration
const shouldShow = ref(!route?.name?.toString().startsWith('index'))
let observer: IntersectionObserver | null = null

const setupObserver = async () => {
  observer?.disconnect()

  // Check if it's home page - route.name can be 'index', 'index___ky', 'index___ru', etc.
  const isHomePage = route?.name?.toString().startsWith('index')
  if (!isHomePage) {
    shouldShow.value = true
    return
  }

  await nextTick()

  if (!stickyRatesTriggerEl?.value) {
    shouldShow.value = false
    return
  }

  observer = new IntersectionObserver(
    ([entry]) => {
      shouldShow.value = !entry.isIntersecting
    },
    { threshold: 0 }
  )

  observer.observe(stickyRatesTriggerEl.value)
}

onMounted(setupObserver)

watch(
  () => route?.path,
  () => {
    nextTick(() => setupObserver())
  }
)

watch(stickyRatesTriggerEl, () => {
  const isHomePage = route?.name?.toString().startsWith('index')
  if (isHomePage) {
    nextTick(() => setupObserver())
  }
})

onBeforeUnmount(() => {
  observer?.disconnect()
})
</script>

<template>
  <div v-if="shouldShow" class="mt-4 border-t bg-white">
    <Container>
      <div class="-mx-4 -mb-5">
        <div class="mx-2 py-1.5 sm:mx-4">
          <section class="flex items-center gap-6 lg:gap-16">
            <div
              class="flex w-full justify-between text-sm [&>article:last-child]:hidden lg:[&>article:last-child]:flex"
            >
              <StickyRatesExchangeRate
                v-for="(currency, code) in fitData"
                :key="code"
                :currency="code.toString()"
                :buy-value="currency?.buyValue"
                :sell-value="currency?.sellValue"
              />
            </div>
            <div class="hidden gap-6 md:flex lg:flex-col lg:justify-end lg:gap-1">
              <Button variant="link" size="auto" as-child>
                <NuxtLinkLocale to="/rates">{{ $t('menu.sticky_rates.view_all') }}</NuxtLinkLocale>
              </Button>
            </div>
          </section>
        </div>
      </div>
    </Container>
  </div>
</template>
